import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  Subject,
  Subscription,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  take,
  takeUntil,
} from 'rxjs';
import { AuthService } from 'src/app/modules/auth/services/auth/auth.service';
import { DemoService } from 'src/app/modules/auth/services/demo/demo.service';
import { CompanySettingsService } from 'src/app/modules/settings/services/company-settings.service';
import { Audience } from '../config/audience.enum';
import { TourFeature } from '../config/tour-feature.enum';
import { TourStateStatus } from '../config/tour-state.enum';
import { TourTrigger } from '../config/trigger.enum';
import { UserMaturity } from '../config/user-maturity.enum';
import { TourConfig } from '../models/tour-config.model';
import { AudienceResolver } from './audience-resolver.service';
import { CompletionDetector } from './completion-detector.service';
import { EligibilityService } from './eligibility.service';
import { FeatureFlagsService } from './feature-flags.service';
import {
  DomainEventPayload,
  OnboardingEventBusService,
} from './onboarding-event-bus.service';
import { TourCoordinator } from './tour-coordinator.service';
import { TourRegistry } from './tour-registry.service';
import { TourStateStore } from './tour-state-store.service';
import { UserPermissionsService } from 'src/app/modules/employee/services/user-permissions.service';

/**
 * Signal-basierter Dispatcher: reagiert auf Login, Route-Changes und
 * Domain-Events und entscheidet pro Tour, ob sie dem User angeboten
 * werden soll.
 *
 * Drei Subscriptions, die parallel laufen:
 * 1. **Login-Eval** (FirstVisit) — `combineLatest(auth, settings)` mit
 *    `take(1)`: einmal pro Session, wenn Login UND CompanySettings geladen
 *    sind. Re-Engagement-Path fuer pausierte Tours.
 * 2. **Route-Change** (RouteChange) — jede `NavigationEnd` evaluiert alle
 *    Tours mit passendem `triggerContext.route`. Debounced (300ms), damit
 *    rasche Navigation (Tab-Switch, Deep-Link-Redirect) nicht Nudge-Spam
 *    erzeugt.
 * 3. **Domain-Events** (AfterSuccess) — haengt am `OnboardingEventBusService`.
 *    Pro eingehendem `domain-event` wird die Tour mit passendem
 *    `triggerContext.requiresSuccessOf` evaluiert.
 *
 * Anti-Spam:
 * - Der `TourCoordinator.offerTour` selbst lehnt parallele Angebote ab
 *   (`offeredTours$`-Guard) und auch Offers waehrend laufender Tour.
 * - Zusaetzlich: pro RouteChange/Event-Trigger wird hoechstens eine Tour
 *   pro Event angeboten (nach Priority sortiert, erste eligible gewinnt).
 * - FirstVisit-Tours werden in der Route-Change-Eval **nicht doppelt**
 *   angeboten — die Evaluierung filtert streng nach dem jeweils
 *   ausloesenden Trigger (`triggers.includes(trigger)`).
 */
@Injectable({ providedIn: 'root' })
export class TriggerMatcher {
  /** Wird bei logout o. a. manuell via `teardown()` aufgeloest. */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly auth: AuthService,
    private readonly registry: TourRegistry,
    private readonly audience: AudienceResolver,
    private readonly state: TourStateStore,
    private readonly flags: FeatureFlagsService,
    private readonly eligibility: EligibilityService,
    private readonly coordinator: TourCoordinator,
    private readonly completionDetector: CompletionDetector,
    private readonly companySettings: CompanySettingsService,
    private readonly router: Router,
    private readonly eventBus: OnboardingEventBusService,
    private readonly userPermissions: UserPermissionsService,
    private readonly demo: DemoService,
  ) {
    this.wireLoginTrigger();
    this.wireRouteChangeTrigger();
    this.wireDomainEventTrigger();
    this.wirePlanningReDetect();
    this.wireDataTourReDetect();
  }

  /**
   * Welcome-Tour erst anbieten, wenn Login UND CompanySettings geladen
   * sind. Ohne das wuerde der CompletionDetector auf leere Daten schauen
   * und der Nudge waere trotz bereits vollstaendigem Setup sichtbar.
   * `take(1)` fuer MVP: pro Session einmal evaluieren. Re-Evaluation nach
   * Logout/Re-Login kommt in einem spaeteren Ausbauschritt.
   */
  private wireLoginTrigger(): void {
    combineLatest([
      this.auth.authState$,
      this.companySettings.CompanySettingsObservable,
    ])
      .pipe(
        filter(
          ([loggedIn, settings]) =>
            !!loggedIn && !!settings && !!settings.CompanyName
        ),
        debounceTime(300),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateLoginContext().catch(() => {});
      });
  }

  /**
   * Reagiert auf jede NavigationEnd. Debounced 300ms, um Redirect-Ketten
   * (Guards, Login-Redirects) nicht als mehrere Trigger zu zaehlen.
   * Startet erst NACH dem Login-Signal — eine Navigation im abgemeldeten
   * Zustand (Login-Page) soll keine Tours anbieten.
   */
  private wireRouteChangeTrigger(): void {
    this.router.events
      .pipe(
        filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd),
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe((ev) => {
        this.evaluateRouteChange(ev.urlAfterRedirects).catch(() => {});
      });
  }

  /**
   * Haengt am Domain-Event-Stream des EventBus. Jedes `domain-event`
   * loest eine Evaluation der AfterSuccess-Tours mit passendem
   * `requiresSuccessOf` aus.
   */
  private wireDomainEventTrigger(): void {
    this.eventBus.domainEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        this.evaluateDomainEvent(payload).catch(() => {});
      });
  }

  /**
   * Re-Detect fuer „Erste Planung" (MentorEntry): Der CompletionDetector
   * markiert den Schritt datengetrieben als erledigt, sobald ein Wochenmuster
   * oder eine Schicht existiert — laeuft aber sonst nur im Login-Eval. Damit
   * der Schritt **ohne Reload** als erledigt erscheint, sobald der User in der
   * laufenden Session plant, stossen wir die Auswertung nach den
   * planungsrelevanten Domain-Events erneut an.
   *
   * Konsistent mit `PlanningReadiness`, der seinen Cache ueber dieselben
   * Events invalidiert. `debounceTime` buendelt Event-Bursts (z. B. mehrere
   * Schichten nacheinander) zu einem Backend-Probe. Der Status-Guard
   * ueberspringt etablierte Konten (Schritt bereits done/dismissed), damit
   * kein unnoetiger GetTemplateList/GetEvents-Call faellt.
   */
  private wirePlanningReDetect(): void {
    const PLANNING_EVENTS = [
      'template.created',
      'shift.created',
      'shift.published',
    ];
    this.eventBus.domainEvents$
      .pipe(
        filter((e) => PLANNING_EVENTS.includes(e.name)),
        debounceTime(300),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const status = this.state.getStateSync(TourFeature.MentorEntry).status;
        if (
          status === TourStateStatus.Completed ||
          status === TourStateStatus.AutoCompleted ||
          status === TourStateStatus.Dismissed
        ) {
          return;
        }
        this.completionDetector.detectFirstPlanning().catch(() => {});
      });
  }

  /**
   * Re-Detect für die binären Daten-Touren (AddSkills/FirstDepartment/
   * FirstEmployee): Der CompletionDetector markiert sie datengetrieben als
   * erledigt, sobald der zugehörige Datensatz existiert — läuft aber sonst nur
   * im Login-Eval. Ohne Re-Detect erscheint nach dem Anlegen weiter ein
   * „Tour fortsetzen?"-Nag (F7), weil die Tour „Started" bleibt. Nach den
   * Daten-Setup-Events stoßen wir die volle Detection erneut an
   * (idempotent + offline-tolerant; die Events sind Admin-Setup-Aktionen).
   */
  private wireDataTourReDetect(): void {
    const DATA_TOUR_EVENTS = [
      'skill.created',
      'department.created',
      'employee.created',
    ];
    this.eventBus.domainEvents$
      .pipe(
        filter((e) => DATA_TOUR_EVENTS.includes(e.name)),
        debounceTime(300),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.completionDetector.detectAndMarkCompleted().catch(() => {});
      });
  }

  /**
   * Evaluiert alle registrierten Touren mit Trigger `FirstVisit` und bietet
   * die erste eligible Tour (nach Priority) dem User als Nudge an.
   *
   * WICHTIG: Der Corner-Nudge ist seit dem Redesign kein primaerer
   * Erst-Einstieg mehr — diese Rolle uebernimmt die Welcome-Card auf dem
   * Dashboard. Der Nudge greift nur noch als Re-Engagement:
   * - User hat die Welcome-Card dismissed/postponed, ist aber noch in
   *   einem Setup-Zwischenstand
   * - User kommt nach einer Pause zurueck und hat eine pausierte Tour
   *
   * Nudges werden daher nur noch ausgespielt, wenn die Tour sich in einem
   * `started`-Zustand befindet (Pause-Resume-Hint) oder wenn die Welcome-
   * Card nicht greift (Audience passt nicht zu Admin/TeamLeader-Flow).
   */
  async evaluateLoginContext(): Promise<void> {
    // Early-Exit: Der Setup-Funnel ist Admin-/TeamLeader-only (alle Tour-
    // Configs deklarieren `audience: [Admin, TeamLeader]`). Reguläre
    // Mitarbeiter und Terminal-Sessions ueberspringen die Backend-Probes,
    // sonst feuert `audience.refreshMaturity` (MaturityFake) und
    // `completionDetector.detectAndMarkCompleted` (checkEmployees) jeweils
    // einen `/Employee/EmployeeList`-Call, fuer den ein normaler Employee
    // keine Berechtigung hat (403, Toast „Unzureichende Berechtigung").
    const loginAudience = await firstValueFrom(this.audience.currentAudience$);
    const isOnboardingAudience =
      loginAudience.includes(Audience.Admin) ||
      loginAudience.includes(Audience.TeamLeader);
    if (!isOnboardingAudience) return;

    await this.audience.refreshMaturity();
    await this.completionDetector.detectAndMarkCompleted();

    const killSwitchActive = await firstValueFrom(
      this.flags.onboardingGloballyDisabled$
    );
    if (killSwitchActive) return;

    const currentAudience = await firstValueFrom(
      this.audience.currentAudience$
    );
    const currentMaturity = await firstValueFrom(
      this.audience.currentMaturity$
    );

    const candidates = this.registry
      .getAll()
      .filter((t) => t.triggers.includes(TourTrigger.FirstVisit))
      .sort((a, b) => a.priority - b.priority);

    // Re-Engagement: bevorzuge Touren, die schon gestartet aber nicht
    // abgeschlossen sind — das ist der Pause-Resume-Case.
    const resumeCandidate = candidates.find((t) => {
      const state = this.state.getStateSync(t.id);
      return state.status === 'started';
    });

    if (resumeCandidate) {
      const state = this.state.getStateSync(resumeCandidate.id);
      const result = this.eligibility.canOffer({
        tour: resumeCandidate,
        audience: currentAudience,
        maturity: currentMaturity,
        permissions: this.userPermissions.CurrentUserPermissions,
        state,
        trigger: TourTrigger.FirstVisit,
        killSwitchActive,
      });
      if (result.eligible) {
        await this.coordinator.offerTour(
          resumeCandidate.id,
          TourTrigger.FirstVisit
        );
      }
    }

    // Kein First-Visit-Nudge mehr: neue Admins sehen stattdessen die
    // Hero-Welcome-Card auf dem Dashboard. Der Nudge bleibt nur ein
    // Re-Engagement-Kanal.

    // Setup-Funnel-Continuity: Wenn ein User mitten im Setup ist (einige
    // Tours completed, andere offen), schicken wir den naechsten Schritt
    // als Nudge raus. Greift unabhaengig vom X-Klick-Cooldown — der war
    // ein "nicht jetzt"-Signal in der vorherigen Session, nicht ein
    // Daueraus. So sieht der User bei jeder neuen Session sofort, wo er
    // weitermachen kann, ohne die FAB-Checklist suchen zu muessen.
    //
    // NICHT in einer Self-Service-Demo. Dort sind vier der fuenf Setup-Tours durch die
    // Seed-Daten automatisch erledigt, uebrig bleibt genau „Unternehmensdaten" — und deren
    // Start navigiert selbst nach `/settings` (`ensureLaunchContext`). Gemessen im August 2026:
    // 10 von 17 Demo-Sitzungen standen binnen 5 bis 20 Sekunden im Stammdaten-Formular. Wer
    // einen Dienstplan sehen wollte, fuellte stattdessen ein Formular aus. Eine vollstaendig
    // bestueckte Demo-Firma braucht keine Einrichtungs-Checkliste; Feature-Discovery-Tours und
    // der Hilfe-Hub bleiben unberuehrt.
    if (!resumeCandidate && !this.demo.isDemoActive()) {
      await this.coordinator.offerNextSetupTour();
    }
  }

  /**
   * Evaluiert RouteChange-Tours fuer die gegebene URL. Bietet die erste
   * eligible Tour (nach Priority) als Nudge an. Der aufrufende
   * Subscription-Teil ist debounced (300ms), siehe `wireRouteChangeTrigger`.
   */
  async evaluateRouteChange(url: string): Promise<void> {
    // Kein Maturity-Refresh hier — das wuerde bei jedem Navigate einen
    // Backend-Call ausloesen. Maturity wird beim Login-Eval refresht und
    // bleibt fuer die Session stabil.
    const killSwitchActive = await firstValueFrom(
      this.flags.onboardingGloballyDisabled$
    );
    if (killSwitchActive) return;

    // Nicht evaluieren, wenn noch nicht eingeloggt — Login-Trigger uebernimmt
    // den ersten Durchgang. Verhindert Nudges auf Login-/Public-Routes.
    if (!this.auth.authState$ || !(await firstValueFrom(this.auth.authState$))) {
      return;
    }

    const currentAudience = await firstValueFrom(
      this.audience.currentAudience$
    );
    const currentMaturity = await firstValueFrom(
      this.audience.currentMaturity$
    );

    const candidates = this.registry
      .getAll()
      .filter((t) => t.triggers.includes(TourTrigger.RouteChange))
      .sort((a, b) => a.priority - b.priority);

    await this.offerFirstEligible(
      candidates,
      currentAudience,
      currentMaturity,
      killSwitchActive,
      TourTrigger.RouteChange,
      { route: url }
    );
  }

  /**
   * Evaluiert AfterSuccess-Tours fuer das gegebene Domain-Event. Bietet
   * die erste passende Tour an (Priority-Sortierung).
   */
  async evaluateDomainEvent(payload: DomainEventPayload): Promise<void> {
    if (!payload?.name) return;

    const killSwitchActive = await firstValueFrom(
      this.flags.onboardingGloballyDisabled$
    );
    if (killSwitchActive) return;

    const currentAudience = await firstValueFrom(
      this.audience.currentAudience$
    );
    const currentMaturity = await firstValueFrom(
      this.audience.currentMaturity$
    );

    // `successCount` aus dem Event-Meta: Gesamtzahl der Entitaeten NACH der
    // ausloesenden Aktion. Wenn der Emitter keinen Count mitliefert, bleibt
    // er `undefined` und die Drosselung unten greift NICHT (rueckwaerts-
    // kompatibler Default — Nudge feuert wie zuvor).
    const successCount =
      typeof payload.meta?.['successCount'] === 'number'
        ? (payload.meta['successCount'] as number)
        : undefined;

    const candidates = this.registry
      .getAll()
      .filter(
        (t) =>
          t.triggers.includes(TourTrigger.AfterSuccess) &&
          t.triggerContext?.requiresSuccessOf === payload.name
      )
      // Min-Success-Count-Gate (UX-Audit findings/04 #2): Eine AfterSuccess-
      // Tour mit `minSuccessCount` wird nur angeboten, wenn das Event eine
      // ausreichend hohe `successCount` meldet. Beispiel: AddSkills feuert
      // erst ab der ZWEITEN Abteilung, damit der „Qualifikationen
      // hinzufuegen"-Nudge den User nicht schon nach Abteilung #1 aus dem
      // Abteilungs-Schritt zieht. Fehlt der Count (Legacy-Emitter), wird
      // nicht gedrosselt.
      .filter((t) => {
        const min = t.triggerContext?.minSuccessCount;
        if (!min || successCount === undefined) return true;
        return successCount >= min;
      })
      .sort((a, b) => a.priority - b.priority);

    await this.offerFirstEligible(
      candidates,
      currentAudience,
      currentMaturity,
      killSwitchActive,
      TourTrigger.AfterSuccess,
      { eventName: payload.name }
    );
  }

  /**
   * Gemeinsame Hilfsfunktion: iteriert die Kandidaten in Priority-Reihenfolge
   * und ruft `offerTour` fuer die erste eligible Tour. Stoppt nach dem
   * ersten Offer (pro Event maximal ein Nudge).
   */
  private async offerFirstEligible(
    candidates: TourConfig[],
    currentAudience: Audience[],
    currentMaturity: UserMaturity,
    killSwitchActive: boolean,
    trigger: TourTrigger,
    extra: { route?: string; eventName?: string }
  ): Promise<void> {
    for (const tour of candidates) {
      const state = this.state.getStateSync(tour.id);
      const result = this.eligibility.canOffer({
        tour,
        audience: currentAudience,
        maturity: currentMaturity,
        permissions: this.userPermissions.CurrentUserPermissions,
        state,
        trigger,
        killSwitchActive,
        route: extra.route,
        eventName: extra.eventName,
      });
      if (result.eligible) {
        await this.coordinator.offerTour(tour.id, trigger);
        return;
      }
    }
  }

  /**
   * Raeumt alle Subscriptions ab. Wird in Tests genutzt, um leaked
   * Subscriptions zwischen Durchlaeufen zu vermeiden.
   */
  teardown(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
